/**
 * "Conecte ao menos dois agentes" (docs/refactor/22-contas-de-agente.md): at the top of Agentes › Conectar
 * agente in the desktop app, while fewer than two of Claude Code and Codex are installed and signed in. One
 * card each, with the three steps said here — create the account (its page opens in the browser), install
 * the agent, sign in (the same sign-in as the row below) — and the card turns green by itself once the app
 * sees the session. With two, a request can have one agent orchestrating and the other helping (front 15).
 */

import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQueries } from '@tanstack/react-query'
import { Check, ExternalLink } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { isDesktop, openExternal } from '../lib/desktop'
import { agentAuthStatus } from '../lib/agentLogin'
import { detectAgents, type DetectedAgent, type LocalAgentId } from '../lib/localAgent'
import { InstallAgentButton } from './InstallAgentButton'

/** Where each agent's account is created; the app installs the agent itself (InstallAgentButton). */
export const AGENT_ACCOUNTS: Array<{ id: LocalAgentId; name: string; signUp: string }> = [
  { id: 'claude', name: 'Claude', signUp: 'https://claude.ai/login' },
  { id: 'codex', name: 'Codex', signUp: 'https://chatgpt.com/codex' },
]

/**
 * Whether an agent is ready: installed, and not known to be signed out. When the agent cannot say (an older CLI,
 * a check still running), it counts as ready — the block is a suggestion, it must not nag about a working agent.
 */
function isReady(agent: DetectedAgent | undefined, signedIn: boolean | null | undefined): boolean {
  return Boolean(agent?.installed) && signedIn !== false
}

/** How many of these agents are ready. */
export function readyCount(agents: DetectedAgent[], signedIn: Record<string, boolean | null | undefined>): number {
  return AGENT_ACCOUNTS.filter((a) => isReady(agents.find((d) => d.id === a.id), signedIn[a.id])).length
}

export function AgentAccountsHint({ agents, onSignIn }: { agents: DetectedAgent[]; onSignIn: (agent: DetectedAgent) => void }) {
  const { t } = useI18n()
  const auth = useQueries({
    queries: AGENT_ACCOUNTS.map((a) => ({ queryKey: ['agent-auth', a.id], queryFn: () => agentAuthStatus(a.id), staleTime: 60_000 })),
  })
  const signedIn = Object.fromEntries(AGENT_ACCOUNTS.map((a, i) => [a.id, auth[i]?.data]))
  const ready = readyCount(agents, signedIn)
  if (ready >= 2) return null

  return (
    <section className="agent-accounts stack" aria-label={t('agentAccounts.title')}>
      <div className="stack" style={{ gap: 2 }}>
        <strong className="small">{ready === 0 ? t('agentAccounts.title') : t('agentAccounts.oneMore')}</strong>
        <span className="tiny muted">{t('agentAccounts.why')}</span>
      </div>
      <div className="agent-accounts__cards">
        {AGENT_ACCOUNTS.map((account) => {
          const detected = agents.find((d) => d.id === account.id)
          const installed = Boolean(detected?.installed)
          const done = isReady(detected, signedIn[account.id])
          return (
            <div key={account.id} className={`agent-accounts__card stack${done ? ' is-done' : ''}`}>
              <strong className="small row row--tight">
                {done && <Check size={14} />} {t('agentAccounts.connect', { name: account.name })}
              </strong>
              {done ? (
                <span className="tiny">{t('agentAccounts.ready')}</span>
              ) : (
                <ol className="tiny agent-accounts__steps">
                  <li>
                    {t('agentAccounts.step.account')}{' '}
                    <button type="button" className="btn btn--sm btn--ghost" onClick={() => void openExternal(account.signUp)}>
                      <ExternalLink size={12} /> {t('agentAccounts.open', { name: account.name })}
                    </button>
                  </li>
                  <li>
                    {installed ? t('agentAccounts.step.installed') : (
                      // One click, the agent's official installer (51, N3); signing in follows by itself.
                      <InstallAgentButton agent={account.id} name={account.name} onInstalled={onSignIn} />
                    )}
                  </li>
                  <li>
                    {t('agentAccounts.step.signIn')}{' '}
                    {installed && detected && (
                      <button type="button" className="btn btn--sm" onClick={() => onSignIn(detected)}>{t('agentAccounts.signIn')}</button>
                    )}
                  </li>
                </ol>
              )}
            </div>
          )
        })}
      </div>
      <span className="tiny muted">{t('agentAccounts.plans')}</span>
    </section>
  )
}

const FIRST_RUN_KEY = 'devpulse.agentAccountsAsked'

/**
 * The first time the desktop app opens with no agent ready, it opens on Agentes › Conectar agente, where the
 * block above says how to get Claude and Codex (docs/refactor/22). Once: after that it is the person's choice.
 */
export function useFirstAgentAccounts(): void {
  const navigate = useNavigate()
  const location = useLocation()
  useEffect(() => {
    if (!isDesktop()) return
    try {
      if (localStorage.getItem(FIRST_RUN_KEY)) return
    } catch {
      return
    }
    let cancelled = false
    void (async () => {
      const agents = await detectAgents().catch(() => [] as DetectedAgent[])
      const status = await Promise.all(AGENT_ACCOUNTS.map((a) => agentAuthStatus(a.id).catch(() => null)))
      if (cancelled) return
      try {
        localStorage.setItem(FIRST_RUN_KEY, '1')
      } catch {
        // Private storage: it may ask again next time, which is harmless.
      }
      const ready = readyCount(agents, Object.fromEntries(AGENT_ACCOUNTS.map((a, i) => [a.id, status[i]])))
      if (ready === 0 && (location.pathname === '/' || location.pathname === '/dashboard')) navigate('/agents/connect?tab=desktop')
    })()
    return () => {
      cancelled = true
    }
    // Once per app start: where the person is afterwards is theirs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
